import styles from "../../styles/Telerecovery.module.css"
import { useContext, useCallback, useState, useEffect, useRef } from "react"
import FBTSidebar from "../../components/sidebars/FBTSidebar"
import {
  meetingProviderOptions,
  getMeetingProviderText,
} from "./Types/TypeUtils"
import { Checkbox } from "primereact/checkbox"
import { InputText } from "primereact/inputtext"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { FileUpload } from "primereact/fileupload"
import { Toast } from "primereact/toast"
import { Dropdown } from "primereact/dropdown"
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { Password } from "primereact/password"
import { Accordion, AccordionTab } from "primereact/accordion"
import { UserContext } from "../../context/userContext"
import { LoaderMedium } from "../../components/Loaders"
import { telerecoveryApiUrl } from "../../config/microservicesUrls"
import { showError, GET_ORGANIZATIONS } from "./Utils"
import { useQuery } from "@apollo/client"
import axios from "axios"
import { Tooltip } from "primereact/tooltip"
import { Message } from "primereact/message"
import {
  requiredFieldIsMissing,
  renderRequireFieldErrorMessage,
  deepClone,
  isEqual,
  stringToBool,
} from "./Utils"
import { MeetingProvider } from "./Types/Enums"
import { LogError } from "../../helpers/logger"

const api_url = telerecoveryApiUrl()

const Configuration = () => {
  const { organizationId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const [errorQueryParam, setErrorQueryParam] = useState(
    searchParams.get("error"),
  )
  const [refreshGoogleToken, setRefreshGoogleToken] = useState(false)
  const toast = useRef(null)
  const userCtx = useContext(UserContext)
  const { user } = userCtx.session
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [integrationSettings, setIntegrationSettings] = useState(null)
  const [showErrors, setShowErrors] = useState(false)
  const [googleMeetActualConfig, setGoogleMeetActualConfig] = useState(null)
  const [googleAuthDialog, setGoogleAuthDialog] = useState({
    visible: false,
    authorizationUrl: null,
  })
  const [organizations, setOrganizations] = useState([])
  const [organization, setOrganization] = useState(null)
  const [orgLoading, setOrgLoading] = useState(false)
  const [activeIndexes, setActiveIndexes] = useState([])

  useEffect(() => {
    setOrganization(organizationId)
  }, [organizationId])

  useEffect(() => {
    const element = document.querySelector(".main-content")
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
    }
  }, [])

  const sortOrganizations = (a, b) => {
    if (a.label < b.label) {
      return -1
    }
    if (a.label > b.label) {
      return 1
    }
    return 0
  }

  const getOrgs = useQuery(GET_ORGANIZATIONS, {
    variables: {
      staff_id: user.staff_id,
      enterprise_id: user.enterprise_id,
    },
    context: {
      headers: {},
    },
    skip: !user,
    fetchPolicy: "network-only",
    onCompleted: ({ organizations }) => {
      const orgs = organizations
        .filter((o) => o.id !== user.enterprise_id)
        .map((o) => ({ id: o.id, name: o.name }))
        .sort(sortOrganizations)
      setOrganizations(orgs)
      const contextOrgIsOrg = user.enterprise_id !== user.organization_id
      if (contextOrgIsOrg && !organizationId) {
        setOrganization(user.organization_id)
      }
    },
  })

  const errorDialog = () => {
    if (errorQueryParam) {
      console.log(
        `Error while setting Google authentication: ${errorQueryParam}`,
      )
    }
    return (
      <Dialog
        header="Error"
        visible={errorQueryParam !== null}
        style={{ width: "50vw" }}
        className="custom_dialog"
        footer={
          <Button
            className={styles.button}
            label="Ok"
            icon="pi pi-check"
            onClick={() => {
              setErrorQueryParam(null)
              setRefreshGoogleToken(true)
              navigate(location.pathname, { replace: true })
            }}
            autoFocus
          />
        }
        modal
        closable={false}
        onHide={() => {}}
      >
        <p>Error while setting Google Authentication. Please try again.</p>
      </Dialog>
    )
  }

  const googleDialog = () => {
    return (
      <Dialog
        header="Information"
        visible={googleAuthDialog.visible}
        style={{ width: "50vw" }}
        className="custom_dialog"
        footer={
          <Button
            className={styles.button}
            label="Ok"
            icon="pi pi-check"
            onClick={() => {
              setGoogleAuthDialog({ ...googleAuthDialog, visible: false })
              window.location.href = googleAuthDialog.authorizationUrl
            }}
            autoFocus
          />
        }
        modal
        closable={false}
        onHide={() => {}}
      >
        <p>You will be redirected to Google for authentication and consent</p>
      </Dialog>
    )
  }

  const getIntegrationSettings = useCallback(() => {
    if (user.enterprise_id) {
      if (!organization) {
        setLoading(true)
      } else {
        setOrgLoading(true)
      }

      const config = {
        headers: {
          Authorization: `Bearer ${userCtx.tokens.getTelerecoveryToken()}`,
        },
      }

      let url = `${api_url}/api/video/settings/${user.enterprise_id}`
      if (organization) {
        url += `/${organization}`
      }
      axios
        .get(url, config)
        .then(async (response) => {
          if (response.status === 200) {
            let data = await response.data
            setIntegrationSettings(data)
            setActiveIndexes(data.meetingServicesConfigurations.map((c, i) => c.isEnabled ? i : -1).filter(i => i !== -1))
            const googleMeetConfig = data.meetingServicesConfigurations
              .find(c => c.provider === MeetingProvider["Google Meet"])
            setGoogleMeetActualConfig(deepClone(googleMeetConfig.attributes))
          }
          setLoading(false)
          setOrgLoading(false)
        })
        .catch(function (response) {
          setLoading(false)
          setOrgLoading(false)
          LogError("Error while getting video integration settings", response)
          const error = response["response"]["data"]["error"]
          showError(toast, "Not able to get settings", error)
        })
    }
  }, [user.enterprise_id, organization])

  useEffect(() => {
    getIntegrationSettings()
  }, [getIntegrationSettings])

  const onFileUpload = (event, provider, attributeId) => {
    const file = event.files[0]
    if (file && file.type === "application/json") {
      const reader = new FileReader()
      reader.onload = (e) => {
        const jsonString = e.target.result
        onAttributeChange(provider, attributeId, jsonString)
      }
      reader.readAsText(file)
    } else {
      console.error("The selected file is not valid JSON file")
    }
  }

  const save = () => {
    if (!organization) {
      setShowErrors(true)
      return
    }
    const enabledProviders =
      integrationSettings.meetingServicesConfigurations.filter(
        (c) => c.isEnabled,
      )
    let allFilled = true
    //Check if all attributes are filled
    enabledProviders.forEach((p) => {
      allFilled &&= p.attributes.every(
        (a) => !requiredFieldIsMissing(a.attributeValue, a.type),
      )
      return
    })
    if (enabledProviders.length > 0) {
      //Check if preferred provider is selected
      allFilled &&=
        integrationSettings.preferedProvider !== null &&
        integrationSettings.preferedProvider !== MeetingProvider.None
    }
    if (!allFilled) {
      setShowErrors(true)
      return
    }

    let googleMeetConfigChanged = refreshGoogleToken

    if (!googleMeetConfigChanged) {
      const googleMeetConfig =
        integrationSettings.meetingServicesConfigurations.find(
          (c) => c.provider === MeetingProvider["Google Meet"],
        )
      if (googleMeetConfig.isEnabled) {
        //Compare Google Meet actual config with the new one to check if it has changed
        googleMeetConfigChanged = !isEqual(
          googleMeetConfig.attributes,
          googleMeetActualConfig,
        )
      }
    }

    setSaving(true)

    //Remove all attributes if provider is disabled
    const updatedConfigurations =
      integrationSettings.meetingServicesConfigurations.map(
        (providerConfig, i) => {
          if (!providerConfig.isEnabled) {
            providerConfig.attributes.forEach((attribute, j) => {
              attribute.attributeValue = ""
            })
          }
          return providerConfig
        },
      )
    let updateIntegrationSettings = { ...integrationSettings }
    updateIntegrationSettings.meetingServicesConfigurations =
      updatedConfigurations
    setIntegrationSettings(updateIntegrationSettings)

    axios
      .put(`${api_url}/api/video/settings`, updateIntegrationSettings, {
        headers: {
          Authorization: `Bearer ${userCtx.tokens.getTelerecoveryToken()}`,
        },
      })
      .then(function (response) {
        setSaving(false)
        if (response.status === 200) {
          toast.current.show({
            severity: "info",
            detail: "Settings updated successfully",
            life: 3000,
          })
          const googleMeetConfig =
            updateIntegrationSettings.meetingServicesConfigurations.find(
              (c) => c.provider === MeetingProvider["Google Meet"],
            )
          if (!googleMeetConfig.isEnabled) {
            googleMeetConfig.attributes.forEach((attribute, j) => {
              attribute.attributeValue = ""
            })
          }
          setGoogleMeetActualConfig(deepClone(googleMeetConfig.attributes))
          if (googleMeetConfigChanged) {
            const googleMeetConfig =
              updateIntegrationSettings.meetingServicesConfigurations.find(
                (c) => c.provider === MeetingProvider["Google Meet"],
              )
            axios
              .post(
                `${api_url}/GoogleAuth/authorize/${user.enterprise_id}/${organization}`,
                googleMeetConfig,
                {
                  headers: {
                    Authorization: `Bearer ${userCtx.tokens.getTelerecoveryToken()}`,
                  },
                },
              )
              .then(function (response) {
                if (response.status === 200) {
                  const { authorizationUrl } = response.data
                  setGoogleAuthDialog({ visible: true, authorizationUrl })
                }
              })
          }
        }
      })
      .catch(function (response) {
        setSaving(false)
        const error = response["response"]["data"]["error"]
        showError(toast, "Not able to update settings", error)
      })
  }

  const onAttributeChange = (provider, attributeId, value) => {
    let updatedIntegrationSettings = { ...integrationSettings }
    const providerIndex =
      updatedIntegrationSettings.meetingServicesConfigurations.findIndex(
        (c) => c.provider === provider,
      )
    const attributeIndex =
      updatedIntegrationSettings.meetingServicesConfigurations[
        providerIndex
      ].attributes.findIndex((a) => a.id === attributeId)
    updatedIntegrationSettings.meetingServicesConfigurations[
      providerIndex
    ].attributes[attributeIndex].attributeValue = value
    setIntegrationSettings(updatedIntegrationSettings)
  }

  const renderHeader = (item, index) => (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Checkbox
        id={item.id}
        className="custom_checkbox"
        value={item.isEnabled}
        style={{ marginRight: "8px" }}
        onChange={(e) => {
          if (e.checked) {
            setActiveIndexes([...activeIndexes, index])
          } else {
            setActiveIndexes(activeIndexes.filter((i) => i !== index))
          }
          const updatedConfigurations =
            integrationSettings.meetingServicesConfigurations.map(
              (providerConfig, i) => {
                if (providerConfig.id === item.id) {
                  providerConfig.isEnabled = e.checked
                }
                return providerConfig
              },
            )
          let updateIntegrationSettings = { ...integrationSettings }
          updateIntegrationSettings.meetingServicesConfigurations =
            updatedConfigurations
          setIntegrationSettings(updateIntegrationSettings)
          setShowErrors(false)
        }}
        disabled={item.provider === integrationSettings.preferedProvider}
        checked={item.isEnabled}
        tooltip={
          item.provider === integrationSettings.preferedProvider
            ? "Cannot disable preferred provider"
            : undefined
        }
        tooltipOptions={{
          className: "custom-tooltip",
          position: "right",
          at: "right center",
        }}
      />
      <label htmlFor={`checkbox-${item.id}`} style={{ flexGrow: 1 }}>
        {getMeetingProviderText(item.provider)}
      </label>
    </div>
  )

  const renderProvider = (item, index) => {
    return (
      <AccordionTab header={renderHeader(item, index)} key={item.id}>
        <div className={`${styles.provider_config} ${styles.settings_panel}`}>
          {item.attributes.map((attribute, i) => {
            return (
              <div key={attribute.id} className={`${styles.field}`}>
                {attribute.type === "JSON" && (
                  <>
                    <FileUpload
                      className={"custom_file_upload"}
                      name={`${item.provider}_${attribute.attributeName}`}
                      customUpload
                      uploadHandler={(e) =>
                        onFileUpload(e, item.provider, attribute.id)
                      }
                      accept="application/json"
                      auto
                      chooseLabel="Browse JSON file"
                      disabled={!item.isEnabled}
                      mode="basic"
                    />
                    {attribute.attributeValue && (
                      <div style={{ width: "600px", overflow: "auto" }}>
                        <p>Content:</p>
                        <pre
                          style={{
                            background: "#f4f4f4",
                            padding: "10px",
                            borderRadius: "5px",
                            fontSize: "10px",
                            whiteSpace: "pre-wrap",
                            wordWrap: "break-word",
                          }}
                        >
                          {JSON.stringify(
                            JSON.parse(attribute.attributeValue),
                            null,
                            2,
                          )}
                        </pre>
                      </div>
                    )}
                    {showErrors &&
                      item.isEnabled &&
                      renderRequireFieldErrorMessage(
                        attribute.attributeValue,
                        `${attribute.attributeName} is required`,
                      )}
                  </>
                )}
                {(attribute.type === "string" ||
                  attribute.type === "email") && (
                  <>
                    <label className={`${styles.label} ${styles.required}`}>
                      {attribute.attributeName}
                    </label>
                    <InputText
                      value={attribute.attributeValue}
                      style={{ width: "100%" }}
                      autoComplete="off"
                      disabled={!item.isEnabled}
                      onChange={(e) =>
                        onAttributeChange(
                          item.provider,
                          attribute.id,
                          e.target.value,
                        )
                      }
                      className={`p-inputtext-md ${showErrors && item.isEnabled && requiredFieldIsMissing(attribute.attributeValue) ? styles.invalid : ""}`}
                    />
                    {showErrors &&
                      item.isEnabled &&
                      renderRequireFieldErrorMessage(
                        attribute.attributeValue,
                        `${attribute.attributeName} is required`,
                      )}
                  </>
                )}
                {attribute.type === "secret" && (
                  <>
                    <label className={`${styles.label} ${styles.required}`}>
                      {attribute.attributeName}
                    </label>
                    <Password
                      value={attribute.attributeValue}
                      toggleMask
                      autoComplete="new-password"
                      disabled={!item.isEnabled}
                      onChange={(e) =>
                        onAttributeChange(
                          item.provider,
                          attribute.id,
                          e.target.value,
                        )
                      }
                      className={`custom_password ${showErrors && item.isEnabled && requiredFieldIsMissing(attribute.attributeValue) ? styles.invalid_pass : ""}`}
                      feedback={false}
                    />
                    {showErrors &&
                      item.isEnabled &&
                      renderRequireFieldErrorMessage(
                        attribute.attributeValue,
                        `${attribute.attributeName} is required`,
                      )}
                  </>
                )}
                {attribute.type === "bool" && (
                  <>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        gap: "5px",
                        marginBottom: "5px",
                        alignItems: "center",
                      }}
                    >
                      <label
                        className={`${styles.label}`}
                        style={{ display: "inline", marginBottom: "0" }}
                      >
                        {attribute.attributeName}
                      </label>
                      {attribute.attributeName === "Reports Scope Enabled" && (
                        <>
                          <i
                            className="pi pi-info-circle"
                            style={{ fontSize: "14px", color: "#8467ad" }}
                            id="scope_info_icon"
                          />
                          <Tooltip
                            target="#scope_info_icon"
                            className="custom-tooltip"
                            position="right"
                          >
                            <span>
                              Enable it only if{" "}
                              <strong>admin.reports.audit.readonly</strong>{" "}
                              scope is enabled in the Google project
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </div>
                    <Checkbox
                      className="custom_checkbox"
                      checked={stringToBool(attribute.attributeValue)}
                      onChange={(e) =>
                        onAttributeChange(
                          item.provider,
                          attribute.id,
                          e.checked.toString(),
                        )
                      }
                      disabled={!item.isEnabled}
                    />
                  </>
                )}
              </div>
            )
          })}
          {item.provider === MeetingProvider["Google Meet"] && (
            <>
              <Message
                severity="info"
                text="You will be redirected to Google for authentication and consent after saving changes"
                className={styles.info_message}
              />
              {googleDialog()}
            </>
          )}
        </div>
      </AccordionTab>
    )
  }

  const mainContent = () => {
    const preferedProviderOptions = meetingProviderOptions().filter((p) => {
      const enabledProviders =
        integrationSettings.meetingServicesConfigurations.filter(
          (c) => c.isEnabled,
        )
      return enabledProviders.some((c) => c.provider === p.value)
    })

    return (
      <>
        <Toast ref={toast} />
        {errorDialog()}
        <div
          className={styles.rounded_wrapper}
          // style={{ minHeight: "1470px" }}
        >
          <div className={`${styles.field} ${styles.settings_panel}`}>
            <label
              className={styles.label}
              style={{ fontSize: "15px", fontWeight: "700" }}
            >
              Organization
            </label>
            <div>
              <Dropdown
                value={organization}
                filter
                options={organizations}
                panelClassName="custom_dropdown_panel"
                onChange={(e) => {
                  setOrganization(e.value)
                }}
                optionLabel="name"
                optionValue="id"
                placeholder="Select an organization"
                style={{ width: "50%", minWidth: "450px" }}
                className={`${styles.selector} ${showErrors && requiredFieldIsMissing(organization) ? styles.invalid : ""}`}
              />
              {showErrors &&
                renderRequireFieldErrorMessage(
                  organization,
                  "Organization is required",
                )}
            </div>
          </div>

          {orgLoading && <LoaderMedium />}

          {!orgLoading && (
            <>
              {integrationSettings.meetingServicesConfigurations.length > 0 && (
                <div className={`${styles.field} ${styles.settings_panel}`}>
                  <label
                    className={styles.label}
                    style={{ fontSize: "15px", fontWeight: "700" }}
                  >
                    Select enabled providers
                  </label>
                  <Accordion
                    className={"custom_accordion_white"}
                    multiple
                    activeIndex={activeIndexes}
                    onTabChange={() => {}}
                    style={{ width: "100%" }}
                  >
                    {integrationSettings.meetingServicesConfigurations.map(
                      (item, index) => {
                        return renderProvider(item, index)
                      },
                    )}
                  </Accordion>
                </div>
              )}
              {preferedProviderOptions.length > 0 && (
                <div className={styles.settings_panel}>
                  <div className={styles.field}>
                    <label
                      className={styles.label}
                      style={{ fontSize: "15px", fontWeight: "700" }}
                    >
                      Select preferred provider
                    </label>
                    <Dropdown
                      value={integrationSettings.preferedProvider}
                      style={{ width: "50%", minWidth: "450px" }}
                      panelClassName="custom_dropdown_panel"
                      options={preferedProviderOptions}
                      onChange={(e) => {
                        let updateIntegrationSettings = {
                          ...integrationSettings,
                        }
                        updateIntegrationSettings.preferedProvider = e.value
                        setIntegrationSettings(updateIntegrationSettings)
                      }}
                      placeholder="Select preferred provider"
                      optionLabel="label"
                      optionValue="value"
                      className={`${styles.selector} ${
                        showErrors &&
                        integrationSettings.preferedProvider ===
                          MeetingProvider.None
                          ? styles.invalid
                          : ""
                      }`}
                    />
                    {showErrors &&
                      integrationSettings.preferedProvider ===
                        MeetingProvider.None && (
                        <small className="p-error" style={{ display: "block" }}>
                          Preferred provider is required
                        </small>
                      )}
                  </div>
                </div>
              )}

              <Button
                className={styles.button}
                type="submit"
                style={{
                  width: "91px!important",
                  justifyContent: "center",
                }}
                disabled={saving || organization === null}
                loading={saving}
                loadingIcon={
                  <i
                    className="pi pi-spin pi-spinner"
                    style={{ order: 1, marginLeft: "0.5em" }}
                  ></i>
                }
                onClick={() => save()}
              >
                Save
              </Button>
            </>
          )}
        </div>
      </>
    )
  }

  return (
    <div className="layout-with-sidebar">
      <div className="sidebar">
        <FBTSidebar />
      </div>
      <div className="main-content">
        <h1 className={styles.title}>Video Integration Settings</h1>
        {(loading || getOrgs.loading) && <LoaderMedium />}
        {!loading && !getOrgs.loading && integrationSettings && mainContent()}
      </div>
    </div>
  )
}

export default Configuration
